// Como o roteiro vira TELA — e por que isto NÃO é o roteiro.
//
// `roteiro.js` é CONTRATO (spec-009 [US-2] AC-2.2): o texto do owner, literal, conferido
// bloco a bloco contra o `BRIEF.md` da spec. Este arquivo não reescreve nada: ele olha
// para os MESMOS blocos e diz que PAPEL cada um cumpre no desenho de 18/09/2026 —
// saudação, parágrafo de introdução, rótulo dos passos, os 4 passos em grade, as duas
// linhas do rodapé e o fechamento.
//
// ★ A FALA NÃO PASSA POR AQUI. `textoFaladoDoRoteiro` continua saindo de
// `blocosDoRoteiro`, sem tocar nesta estrutura. Repartir um passo entre título e
// descrição é decisão de LAYOUT, e `repartirPasso` devolve o `separador` justamente para
// que remontar as partes dê o bloco original byte a byte — há teste dos dois lados.
//
// ★ A ORDEM e a QUANTIDADE dos blocos são preservadas: cada bloco continua sendo um
// `[data-test="boas-vindas-bloco"]` na tela, na mesma posição, porque é o ÍNDICE dele que
// a legenda acende enquanto o áudio anda (`sincronia.js`).

export const PAPEL = Object.freeze({
  SAUDACAO: 'saudacao',
  PARAGRAFO: 'paragrafo',
  ROTULO: 'rotulo',
  PASSO: 'passo',
  LINHA: 'linha',
  FECHAMENTO: 'fechamento',
})

// "1. " — o passo do roteiro sempre abre assim.
const PREFIXO_DO_PASSO = /^(\d+)\.\s+/
// O título do passo termina no primeiro `:` OU no primeiro `.`, o que vier antes: três
// dos quatro passos usam dois-pontos ("Suba sua base de clientes: positivador…") e o
// quarto usa ponto ("Revise e refine na mesma conversa. Se a resposta…").
const SEPARADORES = [':', '.']

function primeiroSeparador(texto) {
  return SEPARADORES.map((marca) => texto.indexOf(marca))
    .filter((indice) => indice !== -1)
    .reduce((menor, indice) => (menor === -1 || indice < menor ? indice : menor), -1)
}

/**
 * Reparte um passo do roteiro em número, título e descrição — ou `null` se o bloco não
 * for um passo.
 *
 * ★ `separador` existe para que `numero + '. ' + titulo + separador + ' ' + descricao`
 * devolva o bloco INTEIRO. É essa igualdade que prova que a tela não editou o roteiro: o
 * que ela faz é repartir, e repartir é reversível.
 */
export function repartirPasso(bloco) {
  const texto = String(bloco || '')
  const prefixo = PREFIXO_DO_PASSO.exec(texto)
  if (!prefixo) return null

  const resto = texto.slice(prefixo[0].length)
  const corte = primeiroSeparador(resto)
  if (corte <= 0) return null

  const descricao = resto.slice(corte + 1).trim()
  // Sem descrição não há o que repartir — o bloco vira parágrafo comum em vez de um
  // cartão com metade vazia.
  if (!descricao) return null

  return {
    numero: prefixo[1],
    titulo: resto.slice(0, corte),
    separador: resto.charAt(corte),
    descricao,
  }
}

// ★ Os papéis saem da POSIÇÃO relativa aos passos, nunca de índices fixos: o roteiro
// pode ganhar ou perder um parágrafo de introdução sem que esta régua precise saber.
// Os passos são a âncora — são os únicos blocos reconhecíveis pela própria forma.
function papelDo({ indice, temPasso, primeiroPasso, ultimoPasso, ultimoIndice }) {
  if (temPasso) return PAPEL.PASSO
  if (indice === 0) return PAPEL.SAUDACAO
  if (indice === ultimoIndice) return PAPEL.FECHAMENTO
  if (primeiroPasso !== -1 && indice === primeiroPasso - 1) return PAPEL.ROTULO
  if (ultimoPasso !== -1 && indice > ultimoPasso) return PAPEL.LINHA
  return PAPEL.PARAGRAFO
}

/**
 * Os blocos do roteiro com o papel de cada um na tela, na MESMA ordem.
 *
 * Sem nenhum passo reconhecido (roteiro reescrito, fixtura mínima), tudo o que está no
 * meio vira parágrafo — a tela degrada para o texto corrido de antes, nunca para o vazio.
 */
export function estruturarRoteiro(blocos) {
  const lista = Array.isArray(blocos) ? blocos : []
  const passos = lista.map(repartirPasso)
  const indicesDePasso = passos.reduce(
    (acumulado, passo, indice) => (passo ? acumulado.concat(indice) : acumulado),
    []
  )
  const primeiroPasso = indicesDePasso.length ? indicesDePasso[0] : -1
  const ultimoPasso = indicesDePasso.length ? indicesDePasso[indicesDePasso.length - 1] : -1
  const ultimoIndice = lista.length - 1

  return lista.map((bloco, indice) => ({
    indice,
    texto: bloco,
    passo: passos[indice],
    papel: papelDo({
      indice,
      temPasso: Boolean(passos[indice]),
      primeiroPasso,
      ultimoPasso,
      ultimoIndice,
    }),
  }))
}
